import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { contrastRatio } from "@/office/colors";
import { readSlideBackgrounds } from "@/office/powerpoint/design";
import {
  allShapes,
  hasTitleText,
  isPlaceholder,
  readPresentation,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionKontrast: Lesson = {
  id: "kontrast",
  title: "Kontrast und Farbe",
  summary: "Lesbar auch bei Sonnenlicht auf dem Beamer – und für Menschen mit Farbsehschwäche.",
  durationMinutes: 10,
  learningGoals: [
    "Den Kontrast zwischen Schrift und Hintergrund einschätzen",
    "Farbe nie als einziges Unterscheidungsmerkmal einsetzen",
    "Die Barrierefreiheitsprüfung von PowerPoint nutzen",
  ],
  steps: [
    {
      id: "kontrastwert",
      title: "Der Kontrastwert",
      explanation: [
        "Barrierefreiheit ist beim Kontrast messbar. Die Richtlinien der WCAG verlangen " +
          "für normalen Text ein Verhältnis von mindestens 4,5:1 zwischen Schrift- und " +
          "Hintergrundfarbe, für großen Text – ab 18 pt fett oder 24 pt normal – " +
          "mindestens 3:1. Auf Folien ist praktisch aller Text in diesem Sinn groß.",
        "Schwarz auf Weiß erreicht 21:1, das Maximum. Ein mittleres Grau auf Weiß liegt " +
          "bei etwa 4:1 und fällt damit schon durch. Hellgrau auf Weiß, das auf dem " +
          "Bildschirm elegant wirkt, ist auf einem Beamer in einem hellen Raum schlicht " +
          "nicht lesbar.",
        "Genau das ist der praktische Punkt: Der Kontrastwert ist keine Vorschrift für " +
          "einen Sonderfall, sondern eine Absicherung für den Normalfall. Ein Raum mit " +
          "Fenstern, ein alter Beamer, ein Zuhörer mit Brille in der letzten Reihe – " +
          "alles Situationen, in denen ein knapper Kontrast kippt.",
        "Vorsicht bei den helleren Abstufungen der Designfarben: Die oberste Zeile der " +
          "Palette hat meist ausreichend Kontrast, die aufgehellten Varianten darunter " +
          "häufig nicht.",
      ],
      keyPoints: [
        "Mindestens 4,5:1 für normalen, 3:1 für großen Text.",
        "Schwarz auf Weiß ist 21:1, mittleres Grau auf Weiß nur etwa 4:1.",
        "Die aufgehellten Stufen der Designfarben fallen oft durch.",
      ],
      task: {
        instruction:
          "Prüfe den Kontrast zwischen deiner Schriftfarbe und dem Folienhintergrund – " +
          "er soll mindestens 4,5:1 betragen.",
        hint: "Wenn du unsicher bist: dunkle Schrift auf hellem Grund oder umgekehrt, ohne Zwischentöne.",
        check: async () => {
          const snapshot = await readPresentation();
          const hintergruende = await readSlideBackgrounds();

          if (hintergruende === null || !snapshot.features.text) {
            return nichtPruefbar(
              "Der Folienhintergrund",
              "1.10",
              "Nutze „Überprüfen“ → „Barrierefreiheit überprüfen“; PowerPoint meldet dort unter „Unzureichender Kontrast“, wo es eng wird.",
            );
          }

          const messbar: Array<{ folie: number; wert: number; farbe: string }> = [];
          for (const folie of snapshot.slides) {
            const hintergrund = hintergruende.find(
              (eintrag) => eintrag.slideIndex === folie.index,
            );
            if (!hintergrund?.color) continue;

            for (const form of folie.shapes) {
              if (!isPlaceholder(form) || form.paragraphs.length === 0) continue;
              const wert = contrastRatio(form.font?.color ?? null, hintergrund.color);
              if (wert === null) continue;
              messbar.push({ folie: folie.index + 1, wert, farbe: form.font?.color ?? "" });
            }
          }

          if (messbar.length === 0) {
            return nichtPruefbar(
              "Die Farbe des Folienhintergrunds",
              "1.10",
              "Die Folien übernehmen ihren Hintergrund vom Folienmaster oder nutzen eine Füllung, deren Farbe sich nicht einzeln auslesen lässt. " +
                "Nutze deshalb „Überprüfen“ → „Barrierefreiheit überprüfen“.",
            );
          }

          const schwach = messbar.filter((eintrag) => eintrag.wert < 4.5);
          if (schwach.length === 0) {
            const schlechtester = messbar.reduce((a, b) => (a.wert <= b.wert ? a : b));
            return pass(
              `Der schwächste gemessene Kontrast liegt bei ${schlechtester.wert.toFixed(1)}:1 auf Folie ${schlechtester.folie} – über der Grenze von 4,5:1.`,
            );
          }

          const schlimmster = schwach.reduce((a, b) => (a.wert <= b.wert ? a : b));
          return fail(
            `Auf Folie ${schlimmster.folie} beträgt der Kontrast nur ${schlimmster.wert.toFixed(1)}:1 ` +
              `(Schriftfarbe ${schlimmster.farbe}).` +
              (schwach.length > 1 ? ` Insgesamt betrifft das ${schwach.length} Textbereiche.` : ""),
            "Wähle eine dunklere Schriftfarbe aus der obersten Zeile der Designpalette – die aufgehellten Stufen darunter reichen selten.",
          );
        },
      },
    },
    {
      id: "farbe-allein",
      title: "Farbe ist nie das einzige Merkmal",
      explanation: [
        "Etwa acht Prozent der Männer und ein halbes Prozent der Frauen haben eine " +
          "Farbsehschwäche, am häufigsten bei Rot und Grün. In einem Raum mit fünfzig " +
          "Personen sitzen also mehrere, für die eine rote und eine grüne Linie im " +
          "Diagramm gleich aussehen.",
        "Die Konsequenz ist nicht, auf Farbe zu verzichten, sondern sie nie allein " +
          "tragen zu lassen. In einem Liniendiagramm bekommen die Linien zusätzlich " +
          "unterschiedliche Stricharten und Datenpunktformen. In einer Tabelle steht " +
          "neben der grünen Zelle ein Häkchen. In einer Ampeldarstellung stehen zusätzlich " +
          "die Wörter.",
        "Der schnellste Test ist ein Screenshot in Graustufen – oder schlicht ein " +
          "Schwarz-Weiß-Ausdruck. Was dort noch unterscheidbar ist, funktioniert für alle.",
        "Dieselbe Überlegung gilt für Formulierungen im Vortrag: „Die rote Linie zeigt“ " +
          "hilft nur, wenn man Rot erkennen kann. „Die obere Linie“ oder „die gestrichelte " +
          "Linie“ funktioniert immer.",
      ],
      keyPoints: [
        "Farbe immer mit einem zweiten Merkmal koppeln: Form, Strichart, Beschriftung.",
        "Test: Screenshot in Graustufen oder Schwarz-Weiß-Ausdruck.",
        "Auch im Vortrag nicht nur über Farbe verweisen.",
      ],
    },
    {
      id: "barrierefreiheitspruefung",
      title: "Die eingebaute Prüfung",
      explanation: [
        "PowerPoint bringt eine eigene Prüfung mit: „Überprüfen“ → „Barrierefreiheit " +
          "überprüfen“. Sie öffnet einen Bereich am rechten Rand und listet die Funde in " +
          "drei Stufen: Fehler, Warnungen und Tipps.",
        "Typische Fehler sind fehlende Alternativtexte und fehlende Folientitel – genau " +
          "die beiden Punkte, um die es in diesem Modul und im ersten Modul geht. " +
          "Typische Warnungen betreffen unzureichenden Kontrast und eine fragwürdige " +
          "Lesereihenfolge.",
        "Jeder Eintrag lässt sich anklicken und führt direkt zur betroffenen Stelle; " +
          "darunter steht, warum es ein Problem ist und wie es sich beheben lässt. " +
          "Praktisch ist auch die Option, die Prüfung dauerhaft in der Statusleiste " +
          "mitlaufen zu lassen – dann meldet sie neue Probleme, während man arbeitet.",
        "Die Prüfung ersetzt kein Nachdenken. Sie erkennt, dass ein Alternativtext " +
          "vorhanden ist, nicht ob er etwas taugt. Aber sie findet zuverlässig alles, was " +
          "schlicht fehlt – und das ist der größte Teil.",
      ],
      keyPoints: [
        "Überprüfen → Barrierefreiheit überprüfen.",
        "Fehler, Warnungen und Tipps mit direktem Sprung zur Fundstelle.",
        "Die Prüfung erkennt Fehlendes, nicht Untaugliches.",
      ],
      task: {
        instruction:
          "Lass die Barrierefreiheitsprüfung laufen und behebe die beiden häufigsten " +
          "Fehler: fehlende Folientitel und fehlende Alternativtexte.",
        hint: "Überprüfen → Barrierefreiheit überprüfen. Jeder Eintrag springt beim Anklicken zur Fundstelle.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Formen und Platzhalter der Folien",
              "1.4",
              "Arbeite die Liste der Barrierefreiheitsprüfung selbst ab.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an.",
            );
          }

          const ohneTitel = snapshot.slides.filter((folie) => !hasTitleText(folie));
          const bildhaft = allShapes(snapshot).filter(
            (form) =>
              form.type === "Image" ||
              form.type === "Chart" ||
              form.type === "SmartArt" ||
              form.placeholder?.containedType === "Image" ||
              form.placeholder?.containedType === "Chart",
          );
          const ohneAltText = bildhaft.filter(
            (form) => (form.altText ?? "").trim().length === 0 && form.isDecorative !== true,
          );

          const kriterien = [
            criterion(
              ohneTitel.length === 0
                ? `Alle ${snapshot.slides.length} Folien haben einen Titel`
                : `Ohne Titel: Folie ${ohneTitel.map((folie) => folie.index + 1).join(", ")}`,
              ohneTitel.length === 0,
            ),
          ];

          if (!snapshot.features.altText) {
            if (ohneTitel.length === 0) {
              return nichtPruefbar(
                "Der Alternativtext einer Form",
                "1.10",
                "Die Folientitel sind vollständig. Die Alternativtexte kontrollierst du über „Überprüfen“ → „Barrierefreiheit überprüfen“.",
              );
            }
          } else {
            kriterien.push(
              criterion(
                bildhaft.length === 0
                  ? "Kein Objekt vorhanden, das einen Alternativtext bräuchte"
                  : ohneAltText.length === 0
                    ? `Alle ${bildhaft.length} Objekte haben einen Alternativtext oder sind dekorativ`
                    : `Ohne Alternativtext: ${ohneAltText.length} Objekte, zuerst auf Folie ${ohneAltText[0].slideIndex + 1}`,
                ohneAltText.length === 0,
              ),
            );
          }

          return fromCriteria(kriterien, {
            success: "Die beiden Fehlerarten, die die Prüfung meldet, sind behoben.",
            failure: "Die Barrierefreiheitsprüfung hätte hier noch etwas zu melden:",
            hint: "Fehlende Titel: „Start“ → „Layout“ oder „Zurücksetzen“. Fehlende Alternativtexte: Rechtsklick auf das Objekt → „Alternativtext bearbeiten“.",
          });
        },
      },
    },
  ],
};
